// Horario comercial en Perú. El reloj del visitante nunca determina cobros ni stock.
export const DIAS_CATALOGO = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MINUTOS_DIA = 1440;
const MINUTOS_SEMANA = MINUTOS_DIA * 7;
const PATRON_FRANJA = /^([01]\d|2[0-3]):([0-5]\d)-(([01]\d|2[0-3]):([0-5]\d)|24:00)$/;
const minutos = hora => Number(hora.slice(0, 2)) * 60 + Number(hora.slice(3));
const horaVisible = minuto => `${String(Math.floor(minuto / 60) % 24).padStart(2, '0')}:${String(minuto % 60).padStart(2, '0')}`;
const intervalosSemana = horario => horario.flatMap((dia, indice) => dia.map(franja => {
  const [desde, hasta] = franja.split('-').map(minutos);
  return { inicio: indice * MINUTOS_DIA + desde,
    fin: indice * MINUTOS_DIA + hasta + (hasta < desde ? MINUTOS_DIA : 0) };
}));

// [] conserva el horario libre de catálogos anteriores. Siete listas activan el reloj.
export const normalizarHorarioSemanal = valor => {
  if (valor == null) return [];
  if (!Array.isArray(valor) || (valor.length !== 0 && valor.length !== 7)) {
    throw new Error('El horario semanal debe incluir los siete días');
  }
  const horario = valor.map((entrada, indice) => {
    // Firestore no acepta arrays directamente anidados. En persistencia cada
    // día es { tramos: [...] }; el formulario y el cálculo usan listas simples.
    const dia = Array.isArray(entrada) ? entrada : entrada?.tramos;
    if (!Array.isArray(dia) || dia.length > 2 || dia.some(franja => typeof franja !== 'string' || !PATRON_FRANJA.test(franja))) {
      throw new Error(`${DIAS_CATALOGO[indice]}: configura hasta dos tramos con horas válidas`);
    }
    if (dia.some(franja => { const [desde, hasta] = franja.split('-'); return desde === hasta; })) {
      throw new Error(`${DIAS_CATALOGO[indice]}: apertura y cierre no pueden coincidir; usa 00:00–24:00 para todo el día`);
    }
    return [...dia].sort();
  });
  const intervalos = intervalosSemana(horario);
  const ampliados = [...intervalos, ...intervalos.map(i => ({ inicio: i.inicio + MINUTOS_SEMANA, fin: i.fin + MINUTOS_SEMANA }))]
    .sort((a, b) => a.inicio - b.inicio);
  if (ampliados.some((intervalo, indice) => indice > 0 && intervalo.inicio < ampliados[indice - 1].fin)) {
    throw new Error('Hay tramos de atención superpuestos; revisa también los que terminan al día siguiente');
  }
  return horario;
};

export const serializarHorarioSemanal = valor => normalizarHorarioSemanal(valor).map(tramos => ({ tramos }));

export const agruparHorarioSemanal = valor => {
  const grupos = [];
  normalizarHorarioSemanal(valor).forEach((tramos, dia) => {
    const firma = tramos.join(' / ');
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.firma === firma) ultimo.fin = dia;
    else grupos.push({ inicio: dia, fin: dia, firma, tramos });
  });
  return grupos.map(g => ({
    dias: g.inicio === g.fin ? DIAS_CATALOGO[g.inicio] : `${DIAS_CATALOGO[g.inicio]} a ${DIAS_CATALOGO[g.fin]}`,
    horas: g.tramos.length ? g.tramos.map(t => t.replace('-', ' – ')).join(' / ') : 'Cerrado'
  }));
};

const relojPeru = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Lima', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const diasIntl = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const momentoPeru = fecha => {
  const partes = Object.fromEntries(relojPeru.formatToParts(fecha).map(p => [p.type, p.value]));
  return diasIntl.indexOf(partes.weekday) * MINUTOS_DIA + Number(partes.hour) * 60 + Number(partes.minute);
};

export const estadoHorarioCatalogo = (valor, ahora = new Date()) => {
  let horario;
  try { horario = normalizarHorarioSemanal(valor); } catch {
    return { configurado: false, abierto: null, etiqueta: 'Consulta el horario con el gimnasio' };
  }
  if (!horario.length) return { configurado: false, abierto: null, etiqueta: '' };
  const intervalos = intervalosSemana(horario);
  if (intervalos.reduce((total, intervalo) => total + intervalo.fin - intervalo.inicio, 0) === MINUTOS_SEMANA) {
    return { configurado: true, abierto: true, etiqueta: 'Abierto las 24 horas' };
  }
  const momento = momentoPeru(ahora);
  const ampliados = [-MINUTOS_SEMANA, 0, MINUTOS_SEMANA].flatMap(desplazamiento => intervalos
    .map(i => ({ inicio: i.inicio + desplazamiento, fin: i.fin + desplazamiento }))).sort((a, b) => a.inicio - b.inicio);
  // Tramos contiguos son una sola atención, incluso al cruzar domingo/lunes.
  const unidos = ampliados.reduce((salida, intervalo) => {
    const ultimo = salida[salida.length - 1];
    if (ultimo && ultimo.fin === intervalo.inicio) ultimo.fin = intervalo.fin;
    else salida.push({ ...intervalo });
    return salida;
  }, []);
  const abierto = unidos.find(i => momento >= i.inicio && momento < i.fin);
  if (abierto) {
    const diferenciaDias = Math.floor(abierto.fin / MINUTOS_DIA) - Math.floor(momento / MINUTOS_DIA);
    const cierreDia = diferenciaDias === 1 ? ' de mañana'
      : diferenciaDias > 1 ? ` del ${DIAS_CATALOGO[Math.floor(abierto.fin / MINUTOS_DIA) % 7].toLowerCase()}` : '';
    return { configurado: true, abierto: true,
      etiqueta: `Abierto hasta las ${horaVisible(abierto.fin)}${cierreDia}` };
  }
  const siguiente = unidos.find(i => i.inicio > momento);
  if (!siguiente) return { configurado: true, abierto: false, etiqueta: 'Cerrado · consulta la próxima atención' };
  const esHoy = Math.floor(siguiente.inicio / MINUTOS_DIA) === Math.floor(momento / MINUTOS_DIA);
  return { configurado: true, abierto: false,
    etiqueta: `Cerrado · abre ${esHoy ? 'hoy' : DIAS_CATALOGO[Math.floor(siguiente.inicio / MINUTOS_DIA) % 7].toLowerCase()} a las ${horaVisible(siguiente.inicio)}` };
};
